import dayjs from 'dayjs';
import { getOrdinalNumber } from './text.utils';
import timezonePlugin from 'dayjs/plugin/timezone';
dayjs.extend(timezonePlugin);
// We have this code as safari is very strict with the value he gets
// safari wont accept for instance 2023-9-12 he needs the full date : 2023-09-12
export function getAbsoluteDate(date, logger, format) {
    if (!date) {
        return null;
    }
    const formattedDate = formatDateWithLeadingZero(date, logger);
    if (format?.includes('hh:mm')) {
        return new Date(formattedDate);
    }
    let newDate = formattedDate;
    // if string is not of ISOString format, make it so.
    if (!isIsoString(newDate)) {
        newDate = new Date(newDate);
        if (Number.isNaN(newDate.getTime())) {
            newDate = new Date(formattedDate.replace('T', ' '));
        }
        // Invalid dates cause RangeError when calling toISOString() - If date is still invalid return null instead of throwing.
        if (Number.isNaN(newDate.getTime())) {
            return null;
        }
        newDate = newDate.toISOString();
    }
    const [dateWithoutTime] = newDate.split('T');
    const [year, month, day] = dateWithoutTime.split('-');
    return new Date(Number(year), Number(month) - 1, Number(day));
}
export function tryToFixDate(date) {
    if (date.length === 8 || date.length === 9 || date.length === 7) {
        const [year, month, day] = date
            .split('-')
            .map(part => part.padStart(2, '0'));
        return `${year}-${month}-${day}`;
    }
    return date;
}
export function getGMTOffset(date = new Date()) {
    const offset = date.getTimezoneOffset();
    const sign = offset > 0 ? '-' : '+'; // Please note the offset sign is opposite. If getTimezoneOffset returns negative it's actually a positive GMT.
    const absoluteOffset = Math.abs(offset);
    const hours = Math.floor(absoluteOffset / 60)
        .toString()
        .padStart(2, '0');
    const minutes = (absoluteOffset % 60).toString().padStart(2, '0');
    return `GMT${sign}${hours}${minutes}`;
}
export function isValidDateType(givenDate) {
    const date = dayjs(givenDate);
    return date.isValid();
}
export const SECOND_IN_MS = 1000;
export const MINUTE_IN_MS = 60 * SECOND_IN_MS;
export const HOUR_IN_MS = 60 * MINUTE_IN_MS;
export const DAY_NAMES = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
];
export const SHORT_DAY_NAMES = [
    'Sun',
    'Mon',
    'Tue',
    'Wed',
    'Thu',
    'Fri',
    'Sat'
];
export const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
];
export const SHORT_MONTH_NAMES = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
];
export const LONG_MONTH_NAMES_FORMAT = 'MMMM';
export const SHORT_MONTH_NAMES_FORMAT = 'MMM';
function transformToDate(date, format, logger) {
    let transformedDate;
    if (!date) {
        return null;
    }
    if (!isValidDateType(date)) {
        transformedDate = tryToFixDate(date);
        if (!isValidDateType(transformedDate)) {
            logger.error(new Error('invalid date passed as a date value'), {
                date: JSON.stringify(date),
                transformedDate: JSON.stringify(transformedDate)
            });
            return null;
        }
    }
    if (typeof date === 'string') {
        transformedDate = getAbsoluteDate(formatDateWithLeadingZero(date, logger), logger, format);
    }
    else if (format?.includes('hh:mm')) {
        transformedDate = date;
    }
    else {
        const transformedMonth = date.getMonth();
        const transformedDay = date.getDate();
        const transformedYear = date.getFullYear();
        transformedDate = new Date(transformedYear, transformedMonth, transformedDay);
    }
    return transformedDate;
}
/*
    This method should be used for display purposes only!
    It does not convert the date into the correct timezone.
    Currently, it has a bug, where if you pass a format that includes hh:mm and you don't pass timezone,
    it will convert the date to local timezone for you.

    The server usually returns UTC dates. Examples:
    2023-11-01T11:19:04.035Z (Z means Zulu time, which is UTC)
    2023-11-01T11:19:04.035+00:00 (same as above, 0 offset from UTC)

    If you encountered a different use case of date format returned from the server please post it on #frontend
*/
export function formatDate(date, format, is24HoursClock = false, timezone = undefined, logger) {
    const transformedDate = transformToDate(date, format, logger);
    if (!transformedDate) {
        return null;
    }
    // Use dayjs to handle timezone adjustment
    let dayjsDate = dayjs(transformedDate);
    if (timezone) {
        dayjsDate = dayjsDate.tz(timezone); // Adjust to the provided timezone
    }
    // Extract date components after timezone adjustment
    const month = dayjsDate.month(); // 0-based index
    const dayOfWeek = dayjsDate.day(); // 0 = Sunday
    const day = dayjsDate.date(); // Day of the month
    const year = dayjsDate.year();
    let hour = dayjsDate.hour(); // Hour of the day (0-23)
    let minutes = dayjsDate.minute().toString().padStart(2, '0');
    const isPM = hour >= 12;
    if (!is24HoursClock) {
        hour = isPM && hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        minutes = `${minutes} ${isPM ? 'PM' : 'AM'}`;
    }
    switch (format) {
        case 'DDD, MM D, YYYY': {
            return `${SHORT_DAY_NAMES[dayOfWeek]}, ${SHORT_MONTH_NAMES[month]} ${day}, ${year}`;
        }
        case 'DDD, MM D, YYYY, hh:mm': {
            return `${SHORT_DAY_NAMES[dayOfWeek]}, ${SHORT_MONTH_NAMES[month]} ${day}, ${year}, ${hour}:${minutes}`;
        }
        case 'DDD, MM D': {
            return `${SHORT_DAY_NAMES[dayOfWeek]}, ${SHORT_MONTH_NAMES[month]} ${day}`;
        }
        case 'MM D, YYYY hh:mm': {
            return `${SHORT_MONTH_NAMES[month]} ${day}, ${year} at ${hour}:${minutes}`;
        }
        case 'DDD MM D YYYY hh:mm:ss GMT': {
            const minutesWithoutAMPM = minutes.substring(0, 2);
            return `${SHORT_DAY_NAMES[dayOfWeek]} ${SHORT_MONTH_NAMES[month]} ${day} ${year} ${String(hour).padStart(2, '0')}:${minutesWithoutAMPM}:00 ${getGMTOffset(transformedDate)}`;
        }
        case 'MMMM D, YYYY hh:mm': {
            return `${MONTH_NAMES[month]} ${day}, ${year} at ${hour}:${minutes}`;
        }
        case 'MMM D, YYYY hh:mm': {
            return `${SHORT_MONTH_NAMES[month]} ${day}, ${year} at ${hour}:${minutes}`;
        }
        case 'D, MM, YYYY, hh:mm': {
            return `${day}, ${SHORT_MONTH_NAMES[month]}, ${year}, ${hour}:${minutes}`;
        }
        case 'MM/dd/yy': {
            return `${month + 1}/${day}/${year.toString().substring(2)}`;
        }
        case 'MMM D, YYYY': {
            return `${MONTH_NAMES[month].substring(0, 3)} ${day}, ${year}`;
        }
        case 'MM D': {
            return `${SHORT_MONTH_NAMES[month]} ${day}`;
        }
        case 'MMM Dd': {
            return `${MONTH_NAMES[month]} ${getOrdinalNumber(day)}`;
        }
        case 'MM D, YYYY': {
            return `${SHORT_MONTH_NAMES[month]} ${day}, ${year}`;
        }
        case 'MM D, YY': {
            return `${SHORT_MONTH_NAMES[month]} ${day}, ${year
                .toString()
                .substring(2)}`;
        }
        case 'YYYY-MM-dd': {
            return `${year}-${month + 1}-${day}`;
        }
        case 'hh:mm': {
            return `${hour}:${minutes}`;
        }
        case 'hh:mm a': {
            return `${hour}:${minutes} ${isPM ? 'pm' : 'am'}`;
        }
        case 'MMM D, hh:mm': {
            return `${SHORT_MONTH_NAMES[month]} ${day}, ${hour}:${minutes}`;
        }
        case 'DD MM, YYYY': {
            return `${getOrdinalNumber(day)} of ${SHORT_MONTH_NAMES[month]}, ${year}`;
        }
        case 'MMMM D "at" hh:mm': {
            return `${MONTH_NAMES[month]} ${getOrdinalNumber(day)} at ${hour}:${minutes.toLowerCase()}`;
        }
        case 'DDDD, MMMM Dd YYYY, "at" hh:mm': {
            return `${DAY_NAMES[dayOfWeek]}, ${MONTH_NAMES[month]} ${getOrdinalNumber(day)} ${year}, at ${hour}:${minutes.toLowerCase()}`;
        }
        case 'MMM D "at" hh:mm': {
            return `${SHORT_MONTH_NAMES[month]} ${day} at ${hour}:${minutes.toLowerCase()}`;
        }
        case LONG_MONTH_NAMES_FORMAT: {
            return `${MONTH_NAMES[month]}`;
        }
        case SHORT_MONTH_NAMES_FORMAT: {
            return `${SHORT_MONTH_NAMES[month]}`;
        }
        case 'MM D, YYYY "·" hh:mm': {
            return `${SHORT_MONTH_NAMES[month]} ${day}, ${year} · ${hour}:${minutes}`;
        }
        case 'MM D, YYYY "-" hh:mm': {
            return `${SHORT_MONTH_NAMES[month]} ${day}, ${year} - ${hour}:${minutes}`;
        }
        case 'DDD, MM D, YYYY "·" hh:mm': {
            return `${SHORT_DAY_NAMES[dayOfWeek]}, ${SHORT_MONTH_NAMES[month]} ${day}, ${year} · ${hour}:${minutes}`;
        }
        case 'DDD D': {
            return `${SHORT_DAY_NAMES[dayOfWeek]} ${day}`;
        }
        case 'DDDD D': {
            return `${DAY_NAMES[dayOfWeek]} ${day}`;
        }
        case 'MMMM D, YYYY':
        default: {
            return `${MONTH_NAMES[month]} ${day}, ${year}`;
        }
    }
}
export function formatDateWithLeadingZero(date, logger) {
    if (!date || typeof date !== 'string') {
        return date;
    }
    const delimiters = ['/', '-'];
    if (!delimiters.some(delimiter => date.includes(delimiter))) {
        return date;
    }
    const delimiter = date.includes('/') ? '/' : '-';
    const splitBy = date.includes('T') ? 'T' : ',';
    const [dateWithoutTime, time] = date.split(splitBy);
    const fixedDate = dateWithoutTime
        .split(delimiter)
        .map(c => (Number(c) < 10 ? '0' : '') + +c)
        .join(delimiter);
    const formattedDateString = time
        ? [fixedDate, time].join(splitBy)
        : fixedDate;
    if (Number.isNaN(new Date(formattedDateString).getTime())) {
        logger.error(new Error('[formatDateWithLeadingZero] Invalid date conversion'), {
            dateInput: date,
            formatttedOutput: formattedDateString,
            ticketId: 'formatDateWithLeadingZero'
        });
    }
    return formattedDateString;
}
function isIsoString(str) {
    if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(str)) {
        return false;
    }
    const d = new Date(str);
    return d.toISOString() === str;
}
export const getCurrentYear = () => {
    return new Date().getFullYear();
};
export var RelativeDays;
(function (RelativeDays) {
    RelativeDays["TODAY"] = "today";
    RelativeDays["TOMORROW"] = "tomorrow";
    RelativeDays["NEXT_MONDAY"] = "next-monday";
    RelativeDays["YESTERDAY"] = "yesterday";
})(RelativeDays || (RelativeDays = {}));
export const padTo2Digits = (num) => {
    return num.toString().padStart(2, '0');
};
export const formatRelativeDate = ({ date, withoutYear = false, timezone, dateFormat }) => {
    if (date) {
        const now = timezone ? dayjs().tz(timezone) : dayjs();
        const targetDate = dayjs(date);
        if (targetDate.isSame(now, 'day')) {
            return RelativeDays.TODAY;
        }
        if (targetDate.isSame(now.add(1, 'day'), 'day')) {
            return RelativeDays.TOMORROW;
        }
        if (targetDate.isSame(now.subtract(1, 'day'), 'day')) {
            return RelativeDays.YESTERDAY;
        }
        const format = dateFormat || (withoutYear ? 'ddd, MMM D' : 'ddd, MMM D, YYYY');
        return targetDate.format(format);
    }
    return '';
};
export const convert24HourTimeStringTo12HourString = (time) => {
    const [_hours, _minutes] = time.split(':');
    if (!_hours || !_minutes) {
        throw new Error('Invalid time format');
    }
    const hours = parseInt(_hours, 10);
    const minutes = parseInt(_minutes, 10);
    if (hours === 24 && minutes === 0) {
        return `12:00 am`;
    }
    if (isNaN(hours) ||
        isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59) {
        throw new Error('Invalid time values');
    }
    const period = hours >= 12 ? 'pm' : 'am';
    const hour12 = hours % 12 || 12;
    return `${hour12}:${padTo2Digits(minutes)} ${period}`;
};
export const convert12HourTimeStringTo24HoursTimeString = (time) => {
    const [_hours, _minutesPart] = time.split(':');
    if (!_minutesPart) {
        throw new Error('Invalid time format');
    }
    const minutePartMatch = _minutesPart.match(/(\d{2})(?:\s*([apAP]\.?[mM]\.?))?$/);
    if (!minutePartMatch) {
        throw new Error('Invalid time format');
    }
    const [, _minutes, period] = minutePartMatch;
    if (!_minutes || !period) {
        throw new Error('Invalid time format');
    }
    const normalizedPeriod = period.replace(/\./g, '').toLowerCase();
    let hours = parseInt(_hours, 10);
    const minutes = parseInt(_minutes, 10);
    if (isNaN(hours) ||
        isNaN(minutes) ||
        hours < 1 ||
        hours > 12 ||
        minutes < 0 ||
        minutes > 59) {
        throw new Error('Invalid time values');
    }
    if (normalizedPeriod !== 'am' && normalizedPeriod !== 'pm') {
        throw new Error('Invalid period');
    }
    if (normalizedPeriod === 'pm' && hours !== 12) {
        hours += 12;
    }
    else if (normalizedPeriod === 'am' && hours === 12) {
        hours = 0;
    }
    return `${padTo2Digits(hours)}:${padTo2Digits(minutes)}`;
};
export function getTimeOptionsForTimepickerDropdown({ isValueIn24hFormat } = {}) {
    const result = [];
    for (let timePart = 0; timePart < 24 * 4; timePart++) {
        let hours = Math.floor(timePart / 4) % 12;
        hours = hours === 0 ? 12 : hours;
        const minutes = ((timePart * 15) % 60).toString().padEnd(2, '0');
        const timeOfDay = timePart / 24 / 2 < 1 ? 'am' : 'pm';
        const resultTime = `${hours}:${minutes} ${timeOfDay}`;
        const value = isValueIn24hFormat
            ? convert12HourTimeStringTo24HoursTimeString(resultTime)
            : resultTime;
        result.push({ value, label: resultTime });
    }
    return result;
}
//# sourceMappingURL=date.utils.js.map